import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, take } from 'rxjs';
import { Paginate } from '@shared/entities/paginate';
import { RequiredDocument } from '@shared/entities/requiredDocument';

export interface RequiredDocumentFilterURL {
  keyword?: string;
  id?: string;
  name?: string;
  displayOrder?: number;
  customerCanUpload?: boolean;
  uploadablePeriodically?: boolean;
  associatedToMachine?: boolean;
  active?: boolean;
  notes?: string;
  templateId?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
  locationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RequiredDocumentService extends ApiService<RequiredDocument> {
  private _refresh$: Subject<Pagination>;
  allRequiredDocuments$: Observable<Paginate<RequiredDocument>>;
  private _currentPaginator: Pagination;
  private _currentFilters: RequiredDocumentFilterURL = {};

  set filters(value: RequiredDocumentFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/required-documents';
    const detailUrl = '/required-documents';

    super(RequiredDocument, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allRequiredDocuments$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.id !== undefined) {
          this.setUrlParameter('id', this._currentFilters.id);
        }
        if (this._currentFilters.name !== undefined) {
          this.setUrlParameter('name', this._currentFilters.name);
        }
        if (this._currentFilters.displayOrder !== undefined) {
          this.setUrlParameter('displayOrder', this._currentFilters.displayOrder.toString());
        }
        if (this._currentFilters.customerCanUpload !== undefined) {
          this.setUrlParameter('customerCanUpload', this._currentFilters.customerCanUpload.toString());
        }
        if (this._currentFilters.uploadablePeriodically !== undefined) {
          this.setUrlParameter('uploadablePeriodically', this._currentFilters.uploadablePeriodically.toString());
        }
        if (this._currentFilters.associatedToMachine !== undefined) {
          this.setUrlParameter('associatedToMachine', this._currentFilters.associatedToMachine.toString());
        }
        if (this._currentFilters.active !== undefined) {
          this.setUrlParameter('active', this._currentFilters.active.toString());
        }
        if (this._currentFilters.notes !== undefined) {
          this.setUrlParameter('notes', this._currentFilters.notes);
        }
        if (this._currentFilters.templateId !== undefined) {
          this.setUrlParameter('templateId', this._currentFilters.templateId);
        }
        const orderBy = this._currentFilters.orderBy || 'displayOrder';
        this.setUrlParameter('order_by', orderBy);

        if (orderBy === 'name') {
          this.setUrlParameter('direction', 'ASC');
        } else if (orderBy === 'displayOrder') {
          this.setUrlParameter('direction', 'ASC');
        } else {
          this.setUrlParameter('direction', 'DESC');
        }
        return this.paginate(RequiredDocument, pagination.page, pagination.perPage);
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allRequiredDocuments$.pipe(take(1));
  }

  refreshFilters() {
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  getList(
    params: {
      page?: number;
      size?: number;
      orderBy?: string;
      direction?: 'ASC' | 'DESC';
      [key: string]: any;
    } = {}
  ): Observable<Paginate<RequiredDocument>> {
    Array.from(this.getUrlParameters().keys()).forEach((key) => this.removeUrlParameter(key));
    if (params.page) this.setUrlParameter('page', params.page.toString());
    if (params.size) this.setUrlParameter('size', params.size.toString());
    if (params.orderBy) this.setUrlParameter('orderBy', params.orderBy);
    if (params.direction) this.setUrlParameter('direction', params.direction);

    Object.keys(params).forEach((key) => {
      if (
        !['page', 'size', 'orderBy', 'direction'].includes(key) &&
        params[key] !== undefined &&
        params[key] !== null
      ) {
        this.setUrlParameter(key, params[key]);
      }
    });
    return this.paginate(RequiredDocument, params.page || 1, params.size || PER_PAGE);
  }
}
